'use client';

import { c as _c } from "react/compiler-runtime";
import { Badge } from '@/shared/components/ui';
import { FeatherAlertTriangle, FeatherCheck, FeatherClock, FeatherHourglass, FeatherPenLine } from '@subframe/core';
import type { AssinaturaAcordoBannerProps, SignatarioCirculo, SignatarioNodeProps } from '../../../types/assinaturaUiTypes';
import { formatAtualizacaoPainel, formatContadorAssinaturas, mapAlertaAssinatura, mapSignatarioParaUi } from '../../../utils/assinaturaUiMapper';
const CIRCULO_CLASSES: Record<SignatarioCirculo, string> = {
  ASSINADO: 'bg-brand-600 text-white',
  NA_VEZ: 'border-2 border-brand-600 bg-white text-brand-600 ring-4 ring-brand-100',
  AGUARDANDO: 'border border-neutral-200 bg-neutral-100 text-neutral-400'
};
const CIRCULO_ICONES: Record<SignatarioCirculo, React.ReactNode> = {
  ASSINADO: <FeatherCheck />,
  NA_VEZ: <FeatherClock />,
  AGUARDANDO: <FeatherHourglass />
};
const informado = (texto: string): boolean => texto.trim().length > 0 && texto.trim().toLocaleLowerCase('pt-BR') !== 'não informado';
function SignatarioNode(t0) {
  const $ = _c(38);
  const {
    signatario,
    isUltimo
  } = t0;
  let t1;
  if ($[0] !== signatario) {
    t1 = mapSignatarioParaUi(signatario);
    $[0] = signatario;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const ui = t1;
  const t2 = `signatario-node-${signatario.ordem}`;
  const t3 = `flex h-10 w-10 items-center justify-center rounded-full text-base ${CIRCULO_CLASSES[ui.circulo]}`;
  const t4 = CIRCULO_ICONES[ui.circulo];
  let t5;
  if ($[2] !== t3 || $[3] !== t4) {
    t5 = <div className={t3}>{t4}</div>;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== isUltimo || $[6] !== signatario.ordem || $[7] !== ui.conectorPreenchido) {
    t6 = !isUltimo && <div data-testid={`signatario-conector-${signatario.ordem}`} className={`min-h-6 w-0.5 flex-1 ${ui.conectorPreenchido ? "bg-brand-600" : "bg-neutral-200"}`} />;
    $[5] = isUltimo;
    $[6] = signatario.ordem;
    $[7] = ui.conectorPreenchido;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = <div className="flex w-10 flex-none flex-col items-center">{t5}{t6}</div>;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = `flex flex-1 items-start justify-between gap-3 ${isUltimo ? "pb-4" : "pb-5"}`;
  let t9;
  if ($[12] !== signatario.rotulo) {
    t9 = informado(signatario.rotulo) ? <span className="text-caption text-subtext-color">{signatario.rotulo}</span> : null;
    $[12] = signatario.rotulo;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== signatario.nome) {
    t10 = informado(signatario.nome) ? <span className="text-body-bold font-body-bold text-default-font">{signatario.nome}</span> : null;
    $[14] = signatario.nome;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t9) {
    t11 = <div className="flex flex-col gap-0.5">{t9}{t10}</div>;
    $[16] = t10;
    $[17] = t9;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== ui.badgeLabel || $[20] !== ui.badgeVariant) {
    t12 = <Badge variant={ui.badgeVariant}>{ui.badgeLabel}</Badge>;
    $[19] = ui.badgeLabel;
    $[20] = ui.badgeVariant;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== ui.detalheDot) {
    t13 = ui.detalheDot && <span className={`h-1.5 w-1.5 rounded-full ${ui.detalheDot === "success" ? "bg-success-600" : "bg-neutral-400"}`} />;
    $[22] = ui.detalheDot;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t13 || $[25] !== ui.detalhe) {
    t14 = <span className="text-caption text-subtext-color flex items-center gap-1">{t13}{ui.detalhe}</span>;
    $[24] = t13;
    $[25] = ui.detalhe;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t12 || $[28] !== t14) {
    t15 = <div className="flex flex-col items-end gap-1">{t12}{t14}</div>;
    $[27] = t12;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t11 || $[31] !== t15 || $[32] !== t8) {
    t16 = <div className={t8}>{t11}{t15}</div>;
    $[30] = t11;
    $[31] = t15;
    $[32] = t8;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t16 || $[35] !== t2 || $[36] !== t7) {
    t17 = <div className="flex gap-4" data-testid={t2}>{t7}{t16}</div>;
    $[34] = t16;
    $[35] = t2;
    $[36] = t7;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  return t17;
}
export function AssinaturaAcordoBanner(t0) {
  const $ = _c(33);
  const {
    acordo,
    signatarios
  } = t0;
  const completo = acordo.statusPainel === "COMPLETO";
  let t1;
  if ($[0] !== signatarios) {
    t1 = formatAtualizacaoPainel(signatarios);
    $[0] = signatarios;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const atualizacao = t1;
  let t2;
  if ($[2] !== acordo.alerta) {
    t2 = acordo.alerta ? mapAlertaAssinatura(acordo.alerta) : null;
    $[2] = acordo.alerta;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const alerta = t2;
  if (signatarios.length === 0) {
    return null;
  }
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="text-brand-600 flex"><FeatherPenLine /></span>;
    t4 = <span className="text-caption-bold font-caption-bold text-default-font tracking-wider uppercase">Assinatura do Acordo</span>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  const t5 = completo ? "success" : "brand";
  const t6 = completo ? "Completo" : "Em andamento";
  let t7;
  if ($[6] !== t5 || $[7] !== t6) {
    t7 = <div className="flex items-center gap-2">{t3}{t4}<Badge variant={t5}>{t6}</Badge></div>;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== acordo) {
    t8 = formatContadorAssinaturas(acordo);
    $[9] = acordo;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t8) {
    t9 = <span className="text-caption text-subtext-color">{t8}</span>;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== atualizacao) {
    t10 = atualizacao && <span className="text-caption text-neutral-400">{atualizacao}</span>;
    $[13] = atualizacao;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t10 || $[16] !== t9) {
    t11 = <div className="flex flex-col items-end">{t9}{t10}</div>;
    $[15] = t10;
    $[16] = t9;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11 || $[19] !== t7) {
    t12 = <div className="flex items-center justify-between gap-3 border-b border-solid border-neutral-100 px-5 py-3.5">{t7}{t11}</div>;
    $[18] = t11;
    $[19] = t7;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== alerta) {
    t13 = alerta && <div data-testid="assinatura-alerta" className="border-warning-200 bg-warning-50 flex items-start gap-2 border-b border-solid px-5 py-3"><span className="text-warning-600 mt-0.5 flex"><FeatherAlertTriangle className="h-4 w-4" /></span><div className="flex flex-col gap-0.5"><span className="text-caption-bold font-caption-bold text-warning-800">{alerta.titulo}</span><span className="text-caption text-warning-700">{alerta.descricao}</span></div></div>;
    $[21] = alerta;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== signatarios) {
    let t15;
    if ($[25] !== signatarios.length) {
      t15 = (signatario, index) => <SignatarioNode key={signatario.id} signatario={signatario} isUltimo={index === signatarios.length - 1} />;
      $[25] = signatarios.length;
      $[26] = t15;
    } else {
      t15 = $[26];
    }
    t14 = signatarios.map(t15);
    $[23] = signatarios;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[27] !== t14) {
    t15 = <div className="px-5 pt-5">{t14}</div>;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t12 || $[30] !== t13 || $[31] !== t15) {
    t16 = <div data-dd-privacy="mask" className="overflow-hidden rounded-[16px] border border-solid border-neutral-200 bg-white shadow-sm">{t12}{t13}{t15}</div>;
    $[29] = t12;
    $[30] = t13;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  return t16;
}